import { Collapsible } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Icon } from './icon';

/**
 * A row that opens to show details, e.g. one endpoint of the API reference or the rarer fields of
 * a form. The whole row is the button; the content stays in the page, so the browser search finds
 * it once open. `open` and `onOpenChange` control it from outside, e.g. a form that opens it for
 * an error in a field inside.
 */
export function Disclosure({
  summary,
  children,
  defaultOpen,
  open,
  onOpenChange,
  className,
}: {
  summary: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}) {
  return (
    <Collapsible.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      className={cn('group/disclosure', className)}
    >
      <Collapsible.Trigger className="flex min-h-control w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-left transition-colors transition-fast hover:bg-hover">
        <Icon
          name="chevron-right"
          className="size-4 shrink-0 text-fg-subtle transition-transform transition-fast group-data-[state=open]/disclosure:rotate-90"
        />
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          {summary}
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content className="px-3 pt-1 pb-4 md:pl-10">{children}</Collapsible.Content>
    </Collapsible.Root>
  );
}
